/*
 * Tab Collections V2
 * Loaded once from layout/theme.liquid. Initializes every tab-collection
 * section, including sections inserted by the Shopify theme editor.
 */
(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-wetheme-section-type="collection--tab-collections"]';
  const instances = new WeakMap();

  class SnapSlider {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.track = wrapper.querySelector('[data-snap-track]');
      this.prevButton = wrapper.querySelector('[data-snap-prev]');
      this.nextButton = wrapper.querySelector('[data-snap-next]');
      this.pagination = wrapper.querySelector('[data-snap-pagination]');
      this.scrollbar = wrapper.querySelector('[data-snap-scrollbar]')
        || wrapper.parentElement?.querySelector('[data-snap-scrollbar]');
      this.thumb = this.scrollbar?.querySelector('[data-snap-scrollbar-thumb]');

      if (!this.track) return;

      this.slidesDesktop = Number.parseInt(wrapper.dataset.slidesDesktop, 10) || 4;
      this.slidesMobile = Number.parseInt(wrapper.dataset.slidesMobile, 10) || 2;
      this.gapDesktop = Number.parseInt(wrapper.dataset.gridSpacingDesktop, 10) || 16;
      this.gapMobile = Number.parseInt(wrapper.dataset.gridSpacingMobile, 10) || 16;
      this.previousViewportWidth = window.innerWidth;
      this.resizeTimer = null;
      this.rafPending = false;

      this.onPreviousClick = () => this.scrollBySlides(-1);
      this.onNextClick = () => this.scrollBySlides(1);
      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onMediaLoad = this.handleMediaLoad.bind(this);
      this.onScrollbarMouseDown = this.handleScrollbarMouseDown.bind(this);
      this.onScrollbarTouchStart = this.handleScrollbarTouchStart.bind(this);

      this.refresh();
      this.attachEvents();
      this.observeProductSlideHeights();
    }

    refresh() {
      if (!this.track) return;

      this.computeLayout();
      this.applyLayout();
      this.syncPromoSlideHeights();
      this.positionNavigation();
      requestAnimationFrame(() => {
        this.syncPromoSlideHeights();
        this.positionNavigation();
      });
      this.updateState();
    }

    destroy() {
      if (!this.track) return;

      clearTimeout(this.resizeTimer);
      this.prevButton?.removeEventListener('click', this.onPreviousClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('load', this.onMediaLoad, true);
      window.removeEventListener('resize', this.onResize);
      this.scrollbar?.removeEventListener('mousedown', this.onScrollbarMouseDown);
      this.scrollbar?.removeEventListener('touchstart', this.onScrollbarTouchStart);
      this.heightObserver?.disconnect();
      this.wrapper._snapSlider = undefined;
    }

    computeLayout() {
      const viewportWidth = window.innerWidth;

      if (viewportWidth >= 1400) {
        this.slidesPerView = this.slidesDesktop;
        this.gap = this.gapDesktop;
        this.peekAmount = this.slidesDesktop + 0.5;
      } else if (viewportWidth >= 768) {
        this.slidesPerView = Math.max(2, this.slidesDesktop - 1);
        this.gap = this.gapDesktop;
        this.peekAmount = this.slidesPerView + 0.5;
      } else {
        this.slidesPerView = this.slidesMobile;
        this.gap = this.gapMobile;
        this.peekAmount = this.slidesMobile === 1
          ? this.slidesMobile + 0.08
          : this.slidesMobile - 1 + 0.5;
      }

      this.slides = Array.from(this.track.children);
      this.slideCount = this.slides.length;
      const hasOverflow = this.slideCount > this.slidesPerView;
      this.effectiveView = hasOverflow ? this.peekAmount : this.slidesPerView;
      this.slideWidth = `calc((100% - ${(this.effectiveView - 1) * this.gap}px) / ${this.effectiveView})`;
    }

    applyLayout() {
      this.track.style.setProperty('--snap-gap', `${this.gap}px`);
      this.track.style.setProperty('--snap-slide-width', this.slideWidth);
      this.track.style.gap = `${this.gap}px`;

      const disabled = this.slideCount <= this.slidesPerView;
      this.wrapper.classList.toggle('snap-slider--disabled', disabled);
      this.wrapper.classList.toggle('swiper-disabled', disabled);
    }

    syncPromoSlideHeights() {
      if (!this.slides) return;

      const promoSlides = this.slides.filter((slide) =>
        slide.classList.contains('tab-collections__promo-slide')
      );

      promoSlides.forEach((promoSlide) => {
        promoSlide.style.removeProperty('height');
        const startIndex = this.slides.indexOf(promoSlide) + 1;
        const adjacentProduct = this.slides
          .slice(startIndex)
          .find((slide) => !slide.classList.contains('tab-collections__promo-slide'));

        if (!adjacentProduct) return;

        const productHeight = Math.ceil(adjacentProduct.getBoundingClientRect().height);
        if (productHeight > 0) {
          promoSlide.style.height = `${productHeight}px`;
          this.fillPromoMediaHeight(promoSlide);
        }
      });
    }

    fillPromoMediaHeight(promoSlide) {
      promoSlide.querySelectorAll('img').forEach((image) => {
        let element = image;
        while (element && element !== promoSlide) {
          element.style.setProperty('height', '100%', 'important');
          element.style.setProperty('max-height', 'none', 'important');
          element = element.parentElement;
        }
      });
    }

    positionNavigation() {
      if (!this.slides) return;

      const navigation = this.wrapper.querySelector('.swiper-btn--wrapper');
      const productSlide = this.slides.find((slide) =>
        !slide.classList.contains('tab-collections__promo-slide')
      );
      const media = productSlide?.querySelector(
        'product-card img, .product-card img, .grid-view-item img, img'
      );

      if (!navigation || !media) return;

      const wrapperRect = this.wrapper.getBoundingClientRect();
      const mediaRect = media.getBoundingClientRect();
      if (wrapperRect.height <= 0 || mediaRect.height <= 0) return;

      const mediaCenter = mediaRect.top - wrapperRect.top + (mediaRect.height / 2);
      this.wrapper.style.setProperty('--snap-navigation-top', `${mediaCenter}px`);
    }

    observeProductSlideHeights() {
      if (!('ResizeObserver' in window) || !this.slides) return;

      this.heightObserver = new ResizeObserver(() => {
        this.syncPromoSlideHeights();
        this.positionNavigation();
      });

      this.slides
        .filter((slide) => !slide.classList.contains('tab-collections__promo-slide'))
        .forEach((slide) => this.heightObserver.observe(slide));
    }

    updateState() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= maxScroll - 1;

      if (this.prevButton) {
        this.prevButton.disabled = atStart;
        this.prevButton.setAttribute('aria-disabled', String(atStart));
      }
      if (this.nextButton) {
        this.nextButton.disabled = atEnd;
        this.nextButton.setAttribute('aria-disabled', String(atEnd));
      }

      this.updateScrollbar(maxScroll);
      this.updatePagination();
    }

    updateScrollbar(maxScroll) {
      if (!this.thumb || !this.scrollbar || maxScroll <= 0) return;

      const scrollbarWidth = this.scrollbar.offsetWidth;
      const thumbWidth = Math.max(
        20,
        scrollbarWidth * (this.track.clientWidth / this.track.scrollWidth)
      );
      const thumbLeft = (this.track.scrollLeft / maxScroll) * (scrollbarWidth - thumbWidth);

      this.thumb.style.width = `${thumbWidth}px`;
      this.thumb.style.left = `${thumbLeft}px`;
    }

    updatePagination() {
      if (!this.pagination) return;

      const slideWidth = this.slides[0]?.offsetWidth + this.gap || 1;
      const currentIndex = Math.round(this.track.scrollLeft / slideWidth);
      const pageCount = Math.max(1, this.slideCount - this.slidesPerView + 1);

      if (this.pagination.children.length !== pageCount) {
        this.pagination.replaceChildren();
        for (let index = 0; index < pageCount; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'swiper-pagination-bullet';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.addEventListener('click', () => this.scrollToIndex(index));
          this.pagination.appendChild(dot);
        }
      }

      Array.from(this.pagination.children).forEach((dot, index) => {
        dot.classList.toggle('swiper-pagination-bullet-active', index === currentIndex);
      });
    }

    scrollToIndex(index) {
      const slideWidth = this.slides[0]?.offsetWidth + this.gap || 0;
      this.track.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
    }

    scrollBySlides(direction) {
      const slideWidth = this.slides[0]?.offsetWidth + this.gap || 1;
      const currentIndex = Math.round(this.track.scrollLeft / slideWidth);
      const target = Math.max(0, Math.min(this.slideCount - 1, currentIndex + direction));
      this.scrollToIndex(target);
    }

    attachEvents() {
      this.prevButton?.addEventListener('click', this.onPreviousClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('load', this.onMediaLoad, true);
      window.addEventListener('resize', this.onResize);
      this.scrollbar?.addEventListener('mousedown', this.onScrollbarMouseDown);
      this.scrollbar?.addEventListener('touchstart', this.onScrollbarTouchStart, { passive: true });
    }

    handleScroll() {
      if (this.rafPending) return;

      this.rafPending = true;
      requestAnimationFrame(() => {
        this.updateState();
        this.rafPending = false;
      });
    }

    handleMediaLoad() {
      this.syncPromoSlideHeights();
      this.positionNavigation();
    }

    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        const currentWidth = window.innerWidth;
        const crossedBreakpoint = (currentWidth < 768) !== (this.previousViewportWidth < 768)
          || (currentWidth < 1400) !== (this.previousViewportWidth < 1400);

        if (Math.abs(currentWidth - this.previousViewportWidth) >= 150 || crossedBreakpoint) {
          this.previousViewportWidth = currentWidth;
          this.refresh();
        }
      }, 150);
    }

    handleScrollbarMouseDown(event) {
      if (event.button !== 0) return;
      this.startScrollbarDrag(event.clientX, false);
    }

    handleScrollbarTouchStart(event) {
      this.startScrollbarDrag(event.touches[0].clientX, true);
    }

    startScrollbarDrag(startX, isTouch) {
      const startScroll = this.track.scrollLeft;
      const moveEvent = isTouch ? 'touchmove' : 'mousemove';
      const endEvent = isTouch ? 'touchend' : 'mouseup';

      const onMove = (event) => {
        const clientX = isTouch ? event.touches[0].clientX : event.clientX;
        const deltaX = clientX - startX;
        const ratio = this.track.scrollWidth / this.scrollbar.offsetWidth;
        this.track.scrollLeft = startScroll + (deltaX * ratio);
      };
      const onEnd = () => {
        document.removeEventListener(moveEvent, onMove);
        document.removeEventListener(endEvent, onEnd);
      };

      document.addEventListener(moveEvent, onMove, isTouch ? { passive: true } : undefined);
      document.addEventListener(endEvent, onEnd, { once: true });
    }
  }

  function initSection(section) {
    if (!section || instances.has(section)) return instances.get(section);

    const tabs = Array.from(section.querySelectorAll('[data-tab-trigger]'));
    const panels = Array.from(section.querySelectorAll('[data-tab-panel]'));
    const sliders = Array.from(section.querySelectorAll('[data-snap-slider-wrapper]'))
      .map((wrapper) => {
        const slider = new SnapSlider(wrapper);
        wrapper._snapSlider = slider;
        return slider;
      });

    const activateTab = (nextTab, { focus = false } = {}) => {
      if (!nextTab) return;

      tabs.forEach((tab) => {
        const active = tab === nextTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      panels.forEach((panel) => {
        const active = panel.id === nextTab.getAttribute('aria-controls');
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;

        if (!active) return;

        panel.style.removeProperty('content-visibility');
        panel.querySelectorAll('[data-snap-slider-wrapper]').forEach((wrapper) => {
          wrapper._snapSlider?.refresh();
        });
        requestAnimationFrame(() => {
          panel.querySelectorAll('product-card').forEach((card) => card.reinitSwatches?.());
        });
      });

      if (focus) nextTab.focus();
    };

    const onTabClick = (event) => activateTab(event.currentTarget);
    const onTabKeydown = (event) => {
      const currentIndex = tabs.indexOf(event.currentTarget);
      let targetIndex = null;

      if (event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = tabs.length - 1;
      if (targetIndex === null) return;

      event.preventDefault();
      activateTab(tabs[targetIndex], { focus: true });
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', onTabClick);
      tab.addEventListener('keydown', onTabKeydown);
    });

    const instance = {
      activateTab,
      destroy() {
        tabs.forEach((tab) => {
          tab.removeEventListener('click', onTabClick);
          tab.removeEventListener('keydown', onTabKeydown);
        });
        sliders.forEach((slider) => slider.destroy());
        instances.delete(section);
      }
    };

    instances.set(section, instance);
    return instance;
  }

  function destroySection(section) {
    instances.get(section)?.destroy();
  }

  function findSection(element) {
    if (!element) return null;
    if (element.matches?.(SECTION_SELECTOR)) return element;

    return element.querySelector?.(SECTION_SELECTOR)
      || element.closest?.(SECTION_SELECTOR)
      || null;
  }

  function initializeExistingSections() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeExistingSections, { once: true });
  } else {
    initializeExistingSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = findSection(event.target);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = findSection(event.target);
    if (section) destroySection(section);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = findSection(event.target);
    const blockId = event.detail?.blockId;
    if (!section || !blockId) return;

    const panel = section.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
    const tabId = panel?.getAttribute('aria-labelledby');
    const tab = tabId ? section.querySelector(`#${CSS.escape(tabId)}`) : null;
    instances.get(section)?.activateTab(tab);
  });
})();
